import { useQuery } from '@apollo/client'
import { get } from 'lodash'
import {
  ARTICLES_WIDGET_LIMIT,
  COLLECTION_WIDGET_LIMIT,
  COLLECTION_SORTER_FIELDS,
  SORTER_DIRECTION,
  PRODUCT_STATUSES,
  PRODUCT_VARIANT_STATUSES,
} from 'constants/common'
import {
  CMS_SHOWROOM_PAGE_BY_SLUG_QUERY,
  CMS_SHOWROOM_PAGE_PREVIEW,
} from 'constants/queries'
import { useUserAuth } from 'providers/userAuth'
import { type CmsPageType, type CmsWidget } from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { isShowroomPage } from '../isShowroomPage'

export const useCmsShowroomPageBySlug = ({
  slug,
  isShowroomIndexPage,
  isPreview = false,
}: {
  slug: string
  isShowroomIndexPage?: boolean
  isPreview?: boolean
}) => {
  const media = useMedia()
  const { isPageLoading } = useUserAuth()
  const [articlesWidgetPageSize, multiCollectionWidgetPageSize] = media.MOBILE
    ? [ARTICLES_WIDGET_LIMIT.MOBILE, COLLECTION_WIDGET_LIMIT.MOBILE]
    : [ARTICLES_WIDGET_LIMIT.DESKTOP, COLLECTION_WIDGET_LIMIT.DESKTOP]

  const { data, loading: isLoading } = useQuery(
    isPreview ? CMS_SHOWROOM_PAGE_PREVIEW : CMS_SHOWROOM_PAGE_BY_SLUG_QUERY,
    {
      fetchPolicy: 'cache-first', // everything should be in cache, so we can use this policy
      skip: isShowroomPage({ slug, isShowroomIndexPage }),
      variables: {
        slug,
        widgetsDefaultInputs: {
          cmsExpertsInput: {
            page: 1,
          },
          cmsArticlesInput: {
            page: 1,
            limit: articlesWidgetPageSize,
          },
          cmsMultiCollectionProductsInput: {
            pagination: {
              page: 1,
              limit: multiCollectionWidgetPageSize,
            },
            sorter: {
              field: COLLECTION_SORTER_FIELDS.POSITION_INDEX_WEIGHT,
              direction: SORTER_DIRECTION.ASC,
            },
            filter: {
              statuses: [PRODUCT_STATUSES.PUBLISHED],
              variantStatuses: [PRODUCT_VARIANT_STATUSES.PUBLISHED],
            },
          },
        },
      },
    }
  )

  const pageBySlug: CmsPageType = isPreview
    ? get(data, ['cmsShowroomPagePreview'])
    : get(data, ['cmsShowroomPageBySlug'])
  const widgets: CmsWidget[] = get(pageBySlug, ['cmsPage', 'widgets'])

  return {
    pageBySlug,
    widgets,
    isLoading: (isPreview && isPageLoading) || isLoading,
  }
}
